import { useCallback, useEffect, useRef, useState } from "react"
import type { UseAutoSaveOptions, UseAutoSaveReturn } from "./useAutoSave.types"

const DEFAULT_DEBOUNCE_MS = 30_000

export const useAutoSave = <T>({
  data,
  onSave,
  enabled = true,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  isValid = true,
  isSaving = false,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn => {
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previousDataRef = useRef<string | null>(null)
  const initialDataRef = useRef<string | null>(null)
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave

  // Capture the initial data on first render to avoid saving unchanged forms
  useEffect(() => {
    if (initialDataRef.current === null) {
      initialDataRef.current = JSON.stringify(data)
    }
  }, [])

  const save = useCallback(async (currentData: T) => {
    setIsAutoSaving(true)
    try {
      await onSaveRef.current(currentData)
      setLastSavedAt(new Date())
    } finally {
      setIsAutoSaving(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled || !isValid || isSaving) {
      return
    }

    const serialized = JSON.stringify(data)

    // Skip if data hasn't changed since last check
    if (serialized === previousDataRef.current) {
      return
    }

    // Skip if data is the same as initial form values (no real changes)
    if (serialized === initialDataRef.current) {
      previousDataRef.current = serialized
      return
    }

    previousDataRef.current = serialized

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    // Set new debounced timer
    timerRef.current = setTimeout(() => {
      void save(data)
    }, debounceMs)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [data, enabled, isValid, isSaving, debounceMs, save])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return { lastSavedAt, isAutoSaving }
}
